import BigNumber from "bignumber.js";
import type { Environment } from "../../types";
import type { StakeRewardRecord } from "./types";

const ISO_DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})/;

/**
 * posa 포트폴리오의 `pickChartAccrued`와 동일한 개인 실현 APR 분자 선택 로직.
 *
 * `/rewards` 레코드를 `reference_date`의 `YYYY-MM-DD` prefix로 일자 버킷팅한 뒤,
 * 오늘 버킷의 `cumulative_earned`(없으면 최신일)를 골라 돌려준다. APR 분자로
 * 이 값을 쓰면, 변동이 큰 온체인 실시간 추정치(earned()) 대신 안정적인 차트
 * 누적값을 사용하게 되어 posa 포트폴리오 My APR과 동일하게 정렬된다.
 *
 * tz offset이 버킷 경계를 밀지 않도록 Date 변환 없이 문자열 prefix를 그대로
 * 버킷 키로 쓴다(posa와 동일). `reference_date`가 없는 레코드는 제외한다.
 * 레코드가 없으면 undefined.
 */
export function pickAccruedCumulativeEarned(
  records: StakeRewardRecord[] | undefined,
): string | undefined {
  if (!records || records.length === 0) return undefined;

  // reference_date prefix -> 해당 일자 버킷의 cumulative_earned.
  // 오름차순(stable) 순회로 set 하여 posa의 bucketByDay와 동일하게 마지막 기록이
  // 남는다. 일자별 스냅샷은 보통 1건이라 순서 영향은 없다.
  const byDay = new Map<string, string>();
  let latestKey: string | undefined;
  for (const { key, cumulative } of records
    .map((r) => ({
      key: ISO_DATE_PREFIX_RE.exec(r.reference_date)?.[1],
      cumulative: r.cumulative_earned,
    }))
    .filter(
      (x): x is { key: string; cumulative: string } =>
        !!x.key && x.cumulative != null,
    )
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))) {
    byDay.set(key, cumulative);
    latestKey = key;
  }
  if (latestKey === undefined) return undefined;

  const today = new Date().toISOString().slice(0, 10);
  return byDay.get(today) ?? byDay.get(latestKey);
}

export const PORTFOLIO_ESTIMATE_TOOLTIP = {
  included: [
    "CROSS Rewards deposited assets",
    "CROSS Rewards claimable rewards",
  ],
  excluded: [
    "Forge LP value",
    "CROSS staking value",
    "Gametoken LP value",
    "Wallet balances outside Portfolio",
  ],
} as const;

export function shouldShowPortfolioEstimateTooltip(env: Environment): boolean {
  return env !== "production";
}

export function formatWalletAddress(address: string): string {
  if (address.length <= 12) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function shortenTransactionHash(hash: string): string {
  if (hash.length <= 13) return hash;
  return `${hash.slice(0, 6)}...${hash.slice(-4)}`;
}

/** `withdrawERC20(uint256 poolId, uint256 amount)` 함수 selector. */
const WITHDRAW_ERC20_SELECTOR = "0xe00e8bf3";

/**
 * `CrossGameRewardRouter.withdrawERC20(poolId, amount)` 호출 calldata 인코딩.
 * keccak 의존성 없이 selector를 상수로 두는 `encodeErc20Transfer`(send)와
 * 동일한 방식. amount는 raw(wei) BigInt.
 */
export function encodeWithdrawErc20(
  poolId: number | bigint,
  amount: bigint,
): `0x${string}` {
  const id = typeof poolId === "bigint" ? poolId : BigInt(poolId);
  if (id < 0n) throw new Error("poolId must be non-negative");
  if (amount < 0n) throw new Error("amount must be non-negative");

  const poolIdWord = id.toString(16).padStart(64, "0");
  const amountWord = amount.toString(16).padStart(64, "0");
  return `${WITHDRAW_ERC20_SELECTOR}${poolIdWord}${amountWord}` as `0x${string}`;
}

/** wei 값을 Gwei 소수 표기로 변환 (가스 상세 행 표기용). */
export function formatGweiFromWei(value?: bigint): string {
  if (!value || value <= 0n) return "0";
  return new BigNumber(value.toString())
    .shiftedBy(-9)
    .toFormat(2, BigNumber.ROUND_DOWN)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

export function formatGasLimit(value?: bigint): string {
  if (!value || value <= 0n) return "0";
  return new BigNumber(value.toString()).toFormat(0, BigNumber.ROUND_DOWN);
}

/**
 * 예상 수수료(native, wei) 표기. 소액이면 7자리, 그 외 4자리 절사 후
 * 트레일링 0 제거.
 */
export function formatNativeFeeFromWei(value?: bigint): string {
  if (!value || value <= 0n) return "0";
  const amount = new BigNumber(value.toString()).shiftedBy(-18);
  const precision = amount.isLessThan(0.0001) ? 7 : 4;
  return amount
    .toFormat(precision, BigNumber.ROUND_DOWN)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");
}

export function formatPortfolioTokenAmount(
  raw: string,
  decimals: number,
): string {
  const amount = new BigNumber(raw).shiftedBy(-decimals);
  if (!amount.isFinite() || amount.isZero()) return "0";
  return amount.isGreaterThanOrEqualTo(1)
    ? amount.toFormat(2, BigNumber.ROUND_DOWN)
    : amount.toFormat(4, BigNumber.ROUND_DOWN);
}

/** 이미 토큰 단위(십진 문자열)로 내려온 금액의 표기용 포맷. wei 변환 없음. */
export function formatPortfolioDecimalAmount(value: string): string {
  return formatPortfolioTokenAmount(value, 0);
}

/**
 * 포트폴리오 금액 표시 통화 설정.
 *
 * - `rate`: USD → 표시 통화 환산 비율(USD 기준). 예) USD=1, EUR=0.92.
 *   host(웹뷰 등)가 선택 통화 비율을 주입한다. 미주입/비유효 시 1(=USD).
 * - `symbol`: 표시 통화 기호. 미주입 시 `"$"`.
 */
export interface PortfolioFiat {
  rate: number;
  symbol: string;
}

/** 통화 미주입 환경의 기본값(USD). */
export const DEFAULT_PORTFOLIO_FIAT: PortfolioFiat = { rate: 1, symbol: "$" };

/**
 * USD BigNumber를 표시 통화로 환산·포맷한다. 소수 2자리 floor로,
 * 기존 USD 표기 규칙(`§4 Display = floor`)을 그대로 유지한다.
 * `rate`가 비유효(<=0/NaN)면 1로 취급해 원 금액을 표기한다.
 */
export function formatFiatFromUsd(
  usd: BigNumber,
  fiat: PortfolioFiat = DEFAULT_PORTFOLIO_FIAT,
): string {
  const rate =
    Number.isFinite(fiat.rate) && fiat.rate > 0 ? fiat.rate : 1;
  const symbol = fiat.symbol || "$";
  return `${symbol}${usd.times(rate).toFormat(2, BigNumber.ROUND_DOWN)}`;
}

/**
 * 포트폴리오 금액의 표시 통화 환산 표기. `amount(raw, decimals) × price`(USD)를
 * `fiat`로 환산해 소수 2자리 절사(floor)로 표기한다.
 *
 * - price가 0/비유효(단가 미상)면 `null` → 호출부가 환산가 줄을 숨긴다.
 *   (단가를 모르면서 "$0.00"으로 오인시키지 않기 위함.)
 * - price는 알지만 금액이 0이면 "0.00"(기호 포함)을 그대로 반환한다.
 * - `fiat` 미주입 시 USD(`$`, rate 1)로 폴백한다.
 */
export function formatPortfolioUsd(
  raw: string,
  decimals: number,
  price: string,
  fiat: PortfolioFiat = DEFAULT_PORTFOLIO_FIAT,
): string | null {
  const p = new BigNumber(price || "0");
  if (!p.isFinite() || p.isLessThanOrEqualTo(0)) return null;
  const usd = new BigNumber(raw || "0").shiftedBy(-decimals).times(p);
  if (!usd.isFinite()) return null;
  return formatFiatFromUsd(usd, fiat);
}
